import { PublicKey, VersionedTransaction } from '@solana/web3.js';
import { Buffer } from 'buffer';
import { getJupiterQuote, getJupiterSwapTx } from './jupiterApi';

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

export const PUBLIC_RPC = 'https://api.mainnet-beta.solana.com';

/** True when no custom RPC is set (the rate-limited public endpoint). */
export const isPublicRpc = (endpoint) =>
  !endpoint || endpoint.trim().replace(/\/+$/, '') === PUBLIC_RPC;

/**
 * Fetch a Jupiter quote and reject it if the estimated price impact exceeds
 * the configured maximum, before any transaction is built or signed.
 */
export const quoteWithImpactGate = async (inputMint, outputMint, amountRaw, slippageBps, apiKey, maxPriceImpactPct) => {
  const quote = await getJupiterQuote(inputMint, outputMint, amountRaw, slippageBps, apiKey);
  const impactPct = parseFloat(quote.priceImpactPct || '0') * 100;
  const max = parseFloat(maxPriceImpactPct);
  if (max > 0 && impactPct > max) {
    throw new Error(`Price impact ${impactPct.toFixed(2)}% exceeds the ${max}% limit — trade aborted.`);
  }
  return quote;
};

/**
 * Build, sign, and robustly send/confirm a Jupiter swap for an already-fetched quote.
 * Returns the `sendAndConfirm` result ({ status, txid, err }).
 */
export const signAndSendSwap = async (connection, wallet, quote, apiKey, priorityFeeSol) => {
  const { swapTransaction, lastValidBlockHeight } = await getJupiterSwapTx(
    quote, wallet.publicKey.toString(), apiKey, priorityFeeSol
  );
  const tx = VersionedTransaction.deserialize(Buffer.from(swapTransaction, 'base64'));
  tx.sign([wallet]);
  return sendAndConfirm(connection, tx, lastValidBlockHeight);
};

/**
 * Robustly send and confirm a signed VersionedTransaction.
 *
 * Sends with skipPreflight, then polls signature status while re-broadcasting
 * the same signed transaction until it is confirmed/finalized or its blockhash
 * expires. Never throws on an indeterminate outcome — returns a status the
 * caller can reconcile against on-chain balances instead of blindly retrying.
 *
 * @returns {{ status: 'confirmed'|'failed'|'expired'|'unknown', txid: string|null, err?: any }}
 */
export const sendAndConfirm = async (connection, signedTx, lastValidBlockHeight, { timeoutMs = 60000, pollMs = 2000 } = {}) => {
  const raw = signedTx.serialize();
  let txid = null;

  try {
    txid = await connection.sendRawTransaction(raw, { skipPreflight: true, maxRetries: 2 });
  } catch (e) {
    // Couldn't even submit — definitively not landed.
    return { status: 'failed', txid: null, err: e?.message || e };
  }

  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    let statusValue = null;
    try {
      const res = await connection.getSignatureStatuses([txid]);
      statusValue = res.value[0];
    } catch {
      // transient RPC error — keep polling
    }

    if (statusValue) {
      if (statusValue.err) return { status: 'failed', txid, err: statusValue.err };
      if (statusValue.confirmationStatus === 'confirmed' || statusValue.confirmationStatus === 'finalized') {
        return { status: 'confirmed', txid };
      }
    }

    // If the blockhash window has passed and it still hasn't landed, it never will.
    if (lastValidBlockHeight) {
      try {
        const height = await connection.getBlockHeight();
        if (height > lastValidBlockHeight) {
          const final = (await connection.getSignatureStatuses([txid])).value[0];
          if (final && !final.err && (final.confirmationStatus === 'confirmed' || final.confirmationStatus === 'finalized')) {
            return { status: 'confirmed', txid };
          }
          return { status: 'expired', txid };
        }
      } catch {
        // ignore and keep trying
      }
    }

    // Re-broadcast to improve landing odds while still valid.
    try {
      await connection.sendRawTransaction(raw, { skipPreflight: true, maxRetries: 2 });
    } catch {
      // ignore — already in flight
    }

    await sleep(pollMs);
  }

  // Couldn't determine outcome within the timeout — caller must reconcile.
  return { status: 'unknown', txid };
};

/** Wallet SOL balance in SOL (not lamports). */
export const getSolBalance = async (connection, pubkey) => {
  const lamports = await connection.getBalance(pubkey);
  return lamports / 1e9;
};

/** Raw SPL token balance (smallest units, as a string) for owner+mint, or "0". */
export const getTokenBalanceRaw = async (connection, owner, mintAddress) => {
  try {
    const accounts = await connection.getParsedTokenAccountsByOwner(owner, { mint: new PublicKey(mintAddress) });
    if (accounts.value.length > 0) {
      return accounts.value[0].account.data.parsed.info.tokenAmount.amount;
    }
  } catch (e) {
    console.warn('getTokenBalanceRaw failed', e);
  }
  return '0';
};

/**
 * Net SOL change (in SOL) for `pubkey` from a confirmed transaction's meta,
 * fee-inclusive. Positive = received (e.g. a sell), negative = spent.
 * Returns null if the transaction/meta can't be fetched.
 */
export const solDeltaFromTx = async (connection, txid, pubkey) => {
  try {
    const tx = await connection.getTransaction(txid, { maxSupportedTransactionVersion: 0, commitment: 'confirmed' });
    if (!tx || !tx.meta) return null;

    const keys = tx.transaction.message.staticAccountKeys
      ? tx.transaction.message.staticAccountKeys
      : tx.transaction.message.accountKeys;
    const target = pubkey.toString();
    const idx = keys.findIndex(k => k.toString() === target);
    if (idx < 0) return null;

    const pre = tx.meta.preBalances[idx];
    const post = tx.meta.postBalances[idx];
    return (post - pre) / 1e9;
  } catch (e) {
    console.warn('solDeltaFromTx failed', e);
    return null;
  }
};
